import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';

import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';

import { Settings, Euro, Calendar, Percent, Trash2, ArrowRightLeft, Archive, RotateCcw, History, BarChart3, Users, Building, Briefcase, Shield, Clock, CheckCircle2, Tag, Hash } from 'lucide-react';

const _API = process.env.REACT_APP_BACKEND_URL;

const _RCDS_TYPES = [
  { id: 'annuel', label: 'RCDS Annuel', icon: Calendar, color: 'emerald' },
  { id: 'nominatif', label: 'RCDS Nominatif', icon: Briefcase, color: 'blue' }
];

const ESPACES = [
  { id: 'super_admin', label: 'Super Admin', icon: Shield },
  { id: 'admin', label: 'Admin', icon: Settings },
  { id: 'cooper', label: "Cooper's", icon: Building },
  { id: 'member', label: 'Membre', icon: Users },
  { id: 'expert', label: 'Expert', icon: Briefcase }
];

export default function BaticoTabsC({ ctx }) {
  const { activeRcds, activites, archives: archives, categories, coefficients: coefficients, config, displayTaxes, formatDate, handleDeletePermanently, handleRestoreArchive, handleSync, history, nomenclatures, remises: remises, saving, selectedEspaces, setSelectedEspaces, stats: stats, taxes: taxes } = ctx;
  return (
    <>
        <TabsContent value="sync">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5 text-blue-500" />
                Synchronisation par Espace
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-5 gap-3">
                {ESPACES.map(espace => (
                  <button
                    key={espace.id}
                    onClick={() => setSelectedEspaces(p => p.includes(espace.id) ? p.filter(e => e !== espace.id) : [...p, espace.id])}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      selectedEspaces.includes(espace.id) ? 'border-blue-500 bg-blue-50' : 'border-slate-200'
                    }`}
                  >
                    <espace.icon className={`h-6 w-6 mx-auto mb-2 ${selectedEspaces.includes(espace.id) ? 'text-blue-600' : 'text-slate-400'}`} />
                    <div className="text-sm font-medium">{espace.label}</div>
                    {selectedEspaces.includes(espace.id) && <CheckCircle2 className="h-4 w-4 text-blue-500 mx-auto mt-2" />}
                  </button>
                ))}
              </div>
              
              <div className="grid grid-cols-4 gap-3">
                <Button variant="outline" onClick={() => handleSync('seuils')} disabled={saving || selectedEspaces.length === 0}>
                  <Euro className="h-4 w-4 mr-2" />Sync Seuils
                </Button>
                <Button variant="outline" onClick={() => handleSync('taxes')} disabled={saving || selectedEspaces.length === 0}>
                  <Percent className="h-4 w-4 mr-2" />Sync Taxes
                </Button>
                <Button variant="outline" onClick={() => handleSync('remises')} disabled={saving || selectedEspaces.length === 0}>
                  <Tag className="h-4 w-4 mr-2" />Sync Remises
                </Button>
                <Button variant="outline" onClick={() => handleSync('coefficients')} disabled={saving || selectedEspaces.length === 0}>
                  <Hash className="h-4 w-4 mr-2" />Sync Coefs
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* HISTORIQUE */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5 text-slate-500" />
                Historique des modifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {history.map((entry, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Settings className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{entry.action}</div>
                      <div className="text-xs text-slate-500">{entry.details}</div>
                    </div>
                    <div className="text-xs text-slate-400 text-right">
                      <div>{formatDate(entry.timestamp)}</div>
                      <div>{entry.user}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ARCHIVES */}
        <TabsContent value="archives">
          <div className="space-y-6">
            {/* Taxes archivées */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Archive className="h-5 w-5 text-orange-500" />
                  Taxes archivées - RCDS {activeRcds === 'annuel' ? 'Annuel' : 'Nominatif'}
                </CardTitle>
                <CardDescription>
                  Taxes désactivées qui peuvent être restaurées
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(config?.taxes?.[activeRcds]?.filter(t => t.archived) || []).length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Archive className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Aucune taxe archivée</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(config?.taxes?.[activeRcds]?.filter(t => t.archived) || []).map(taxe => (
                      <div key={taxe.id} className="flex items-center justify-between p-4 bg-orange-50 rounded-xl border border-orange-200">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-10 bg-orange-100 text-orange-700 rounded-lg flex items-center justify-center font-bold text-sm">
                            {taxe.taux}%
                          </div>
                          <div>
                            <div className="font-medium text-slate-700">{taxe.nom}</div>
                            <div className="text-xs text-slate-500">
                              Archivé le {formatDate(taxe.archived_at)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => handleRestoreArchive('taxes', taxe.id)}
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Restaurer
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeletePermanently('taxes', taxe.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Remises archivées */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Archive className="h-5 w-5 text-green-500" />
                  Remises archivées
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(config?.remises?.[activeRcds]?.filter(r => r.archived) || []).length === 0 ? (
                  <div className="text-center py-6 text-slate-400">
                    <p className="text-sm">Aucune remise archivée</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(config?.remises?.[activeRcds]?.filter(r => r.archived) || []).map(remise => (
                      <div key={remise.id} className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-200">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-10 bg-green-100 text-green-700 rounded-lg flex items-center justify-center font-bold text-sm">
                            -{remise.pourcentage}%
                          </div>
                          <div>
                            <div className="font-medium text-slate-700">{remise.nom}</div>
                            <div className="text-xs text-slate-500">{remise.conditions}</div>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-green-600 hover:text-green-700"
                          onClick={() => handleRestoreArchive('remises', remise.id)}
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Restaurer
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Résumé archives */}
            <Card className="bg-slate-50">
              <CardContent className="pt-6">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-4 bg-white rounded-xl">
                    <div className="text-2xl font-bold text-orange-600">
                      {(config?.taxes?.[activeRcds]?.filter(t => t.archived) || []).length}
                    </div>
                    <div className="text-sm text-slate-500">Taxes archivées</div>
                  </div>
                  <div className="p-4 bg-white rounded-xl">
                    <div className="text-2xl font-bold text-green-600">
                      {(config?.remises?.[activeRcds]?.filter(r => r.archived) || []).length}
                    </div>
                    <div className="text-sm text-slate-500">Remises archivées</div>
                  </div>
                  <div className="p-4 bg-white rounded-xl">
                    <div className="text-2xl font-bold text-slate-600">
                      {config?.stats?.total_archives || 0}
                    </div>
                    <div className="text-sm text-slate-500">Total archivages</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* STATISTIQUES */}
        <TabsContent value="stats">
          <div className="space-y-6">
            {/* Vue d'ensemble */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-100 text-sm">Modifications totales</p>
                      <p className="text-3xl font-bold">{config?.stats?.total_modifications || 0}</p>
                    </div>
                    <Settings className="h-10 w-10 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-500 to-indigo-500 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Synchronisations</p>
                      <p className="text-3xl font-bold">{config?.stats?.total_syncs || 0}</p>
                    </div>
                    <ArrowRightLeft className="h-10 w-10 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Taxes actives</p>
                      <p className="text-3xl font-bold">{displayTaxes.length}</p>
                    </div>
                    <Percent className="h-10 w-10 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-500 to-emerald-500 text-white">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">Activités</p>
                      <p className="text-3xl font-bold">{activites.length}</p>
                    </div>
                    <Briefcase className="h-10 w-10 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Détails par type RCDS */}
            <div className="grid grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-emerald-700">
                    <Calendar className="h-5 w-5" />
                    RCDS Annuel
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Seuil de CA d'éligibilité</span>
                    <span className="font-bold text-emerald-700">
                      {(config?.seuils_ca_eligibilite?.annuel?.seuil_ca ?? 0).toLocaleString('fr-FR', {minimumFractionDigits: 0, maximumFractionDigits: 0})} €
                      {config?.seuils_ca_eligibilite?.annuel?.actif ? '' : ' (inactif)'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Montant de base contribution</span>
                    <span className="font-bold">
                      {(config?.seuils_ca?.annuel?.seuil_minimum ?? 0).toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Taxes actives</span>
                    <span className="font-bold">{(config?.taxes?.annuel?.filter(t => t.actif && !t.archived) || []).length}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Total taxes</span>
                    <span className="font-bold text-purple-600">
                      {(config?.taxes?.annuel?.filter(t => t.actif && !t.archived) || []).reduce((s, t) => s + (t.taux || 0), 0).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Remises actives</span>
                    <span className="font-bold">{(config?.remises?.annuel?.filter(r => r.actif && !r.archived) || []).length}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-700">
                    <Briefcase className="h-5 w-5" />
                    RCDS Nominatif
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Seuil de CA d'éligibilité</span>
                    <span className="font-bold text-emerald-700">
                      {(config?.seuils_ca_eligibilite?.nominatif?.seuil_ca ?? 0).toLocaleString('fr-FR', {minimumFractionDigits: 0, maximumFractionDigits: 0})} €
                      {config?.seuils_ca_eligibilite?.nominatif?.actif ? '' : ' (inactif)'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Montant de base contribution</span>
                    <span className="font-bold">
                      {(config?.seuils_ca?.nominatif?.seuil_minimum ?? 0).toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Taxes actives</span>
                    <span className="font-bold">{(config?.taxes?.nominatif?.filter(t => t.actif && !t.archived) || []).length}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Total taxes</span>
                    <span className="font-bold text-purple-600">
                      {(config?.taxes?.nominatif?.filter(t => t.actif && !t.archived) || []).reduce((s, t) => s + (t.taux || 0), 0).toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Remises actives</span>
                    <span className="font-bold">{(config?.remises?.nominatif?.filter(r => r.actif && !r.archived) || []).length}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Statistiques d'utilisation */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-indigo-500" />
                  Utilisation des configurations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-4">
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <div className="text-2xl font-bold text-slate-700">{nomenclatures.length}</div>
                    <div className="text-xs text-slate-500">Nomenclatures</div>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <div className="text-2xl font-bold text-slate-700">{categories.length}</div>
                    <div className="text-xs text-slate-500">Catégories</div>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <div className="text-2xl font-bold text-slate-700">{activites.length}</div>
                    <div className="text-xs text-slate-500">Activités</div>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <div className="text-2xl font-bold text-slate-700">{(config?.types_operation || []).length}</div>
                    <div className="text-xs text-slate-500">Types opération</div>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <div className="text-2xl font-bold text-slate-700">{(config?.territoires_intervention || []).length}</div>
                    <div className="text-xs text-slate-500">Territoires</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dernière mise à jour */}
            <Card className="bg-slate-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">Dernière modification</p>
                      <p className="text-xs text-slate-500">{formatDate(config?.updated_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">Modifié par</p>
                      <p className="text-xs text-slate-500">{config?.updated_by || 'Système'}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-slate-600">
                    Version {config?.version || '3.0.0'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
    </>
  );
}
